import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { formatDateToString } from "go-core/components/Formatters/FormatDate";
import { useWindowSize } from "go-core/components/useWindowSize";
import { selectOrganization } from "go-security/services/organizations/selectors";
import {
	BundleApi,
	OrderApi,
	PurchaseApi,
	StockAdjustmentApi,
	TransferApi,
} from "../../../../../../../services/Api/Organization/types";

interface Props {
	show: boolean;
	onHide: () => void;
	document: PurchaseApi | TransferApi | OrderApi | StockAdjustmentApi | BundleApi;
	type: "purchase" | "transfer" | "order" | "stockAdjustment" | "bundle";
}

interface FormProps {
	referenceNumber: boolean;
	stock: boolean;
	contact: boolean;
	comment: boolean;
	customFields: boolean;
	items: {
		quantity: boolean;
		volume: boolean;
		price: boolean;
		tax: boolean;
	};
	tags: boolean;
}

const CopyDocumentModal = ({ onHide, show, document, type }: Props) => {
	const { t } = useTranslation();
	const { isSmallBreakpoint } = useWindowSize();
	const [form, setForm] = useState<FormProps>({
		referenceNumber: false,
		stock: true,
		contact: true,
		comment: true,
		customFields: true,
		items: {
			quantity: true,
			tax: true,
			price: true,
			volume: true,
		},
		tags: false,
	});
	const history = useHistory();
	const organization = useSelector(selectOrganization);

	const handleOnClickFormCheck = (field: string) => {
		setForm({
			...form,
			[field]: !form[field],
		});
	};

	const handleOnClickFormItemsCheck = (field: "quantity" | "tax" | "price" | "volume") => {
		setForm({
			...form,
			items: {
				...form.items,
				[field]: !form.items[field],
			},
		});
	};

	const onSubmit = () => {
		if (type === "purchase") {
			const purchaseDocument = JSON.parse(JSON.stringify(document)) as PurchaseApi;
			const copiedItem = {
				purchased_at: formatDateToString(new Date()),
				reference_number: form.referenceNumber ? purchaseDocument.reference_number : "",
				file_attachments: [],
				id: null,
				custom_fields: form.customFields ? purchaseDocument.custom_fields : [],
				stock: form.stock ? purchaseDocument.stock : undefined,
				stock_id: form.stock ? purchaseDocument.stock_id : undefined,
				comment: form.comment ? purchaseDocument.comment : "",
				tags: form.tags ? purchaseDocument.tags : [],
				items: [
					...purchaseDocument.items.map((item) => {
						return {
							...item,
							quantity: form.items.quantity ? item.quantity : undefined,
							volume: form.items.volume ? item.volume : undefined,
							price_net_money: form.items.price ? item.price_net_money : undefined,
							tax: form.items.tax ? item.tax : undefined,
							tax_id: form.items.tax ? item.tax_id : undefined,
						};
					}),
				],
				contact: form.contact ? purchaseDocument.contact : undefined,
				contact_id: form.contact ? purchaseDocument.contact_id : undefined,
			};

			history.push({
				pathname: `/${organization.id}/documents/purchases/new`,
				state: {
					copiedItem,
				},
			});
		}

		if (type === "transfer") {
			const transferDocument = JSON.parse(JSON.stringify(document)) as TransferApi;
			const copiedItem = {
				transferred_at: formatDateToString(new Date()),
				reference_number: form.referenceNumber ? transferDocument.reference_number : "",
				file_attachments: [],
				id: null,
				custom_fields: form.customFields ? transferDocument.custom_fields : [],
				source_stock: form.stock ? transferDocument.source_stock : undefined,
				source_stock_id: form.stock ? transferDocument.source_stock_id : undefined,
				destination_stock: form.stock ? transferDocument.destination_stock : undefined,
				destination_stock_id: form.stock ? transferDocument.destination_stock_id : undefined,
				comment: form.comment ? transferDocument.comment : "",
				tags: form.tags ? transferDocument.tags : [],
				items: [
					...transferDocument.items.map((item) => {
						return {
							...item,
							quantity: form.items.quantity ? item.quantity : undefined,
							volume: form.items.volume ? item.volume : undefined,
						};
					}),
				],
			};

			history.push({
				pathname: `/${organization.id}/documents/transfers/new`,
				state: {
					copiedItem,
				},
			});
		}

		if (type === "bundle") {
			const bundleDocument = JSON.parse(JSON.stringify(document)) as BundleApi;
			const copiedItem = {
				bundled_at: formatDateToString(new Date()),
				reference_number: form.referenceNumber ? bundleDocument.reference_number : "",
				file_attachments: [],
				id: null,
				custom_fields: form.customFields ? bundleDocument.custom_fields : [],
				source_stock: form.stock ? bundleDocument.source_stock : undefined,
				source_stock_id: form.stock ? bundleDocument.source_stock_id : undefined,
				destination_stock: form.stock ? bundleDocument.destination_stock : undefined,
				destination_stock_id: form.stock ? bundleDocument.destination_stock_id : undefined,
				comment: form.comment ? bundleDocument.comment : "",
				tags: form.tags ? bundleDocument.tags : [],
				contact: form.contact ? bundleDocument.contact : undefined,
				contact_id: form.contact ? bundleDocument.contact_id : undefined,
				items: [
					...bundleDocument.items.map((item) => {
						return {
							...item,
							quantity: form.items.quantity ? item.quantity : undefined,
							quantity_produced: form.items.quantity ? item.quantity : undefined,
							items: [
								...(item.items || []).map((itemItem) => ({
									...itemItem,
									product_id: itemItem.product_id,
									product_name: itemItem.product?.name,
									measure_type: itemItem.product?.measure_type,
									state: itemItem.product?.state,
									unit_fifo_cost_net_money: itemItem.product?.cost_price_money,
									quantity: form.items.quantity ? itemItem.quantity : undefined,
								})),
							],
						};
					}),
				],
			};

			history.push({
				pathname: `/${organization.id}/documents/bundles/new`,
				state: {
					copiedItem,
				},
			});
		}

		if (type === "order") {
			const orderDocument = JSON.parse(JSON.stringify(document)) as OrderApi;
			const copiedItem = {
				ordered_at: formatDateToString(new Date()),
				reference_number: form.referenceNumber ? orderDocument.reference_number : "",
				file_attachments: [],
				id: null,
				custom_fields: form.customFields ? orderDocument.custom_fields : [],
				stock: form.stock ? orderDocument.stock : undefined,
				stock_id: form.stock ? orderDocument.stock_id : undefined,
				comment: form.comment ? orderDocument.comment : "",
				tags: form.tags ? orderDocument.tags : [],
				items: [
					...orderDocument.items.map((item) => {
						return {
							...item,
							quantity: form.items.quantity ? item.quantity : undefined,
							volume: form.items.volume ? item.volume : undefined,
							price_net_money: form.items.price ? item.price_net_money : undefined,
							tax: form.items.tax ? item.tax : undefined,
							tax_id: form.items.tax ? item.tax_id : undefined,
						};
					}),
				],
				contact: form.contact ? orderDocument.contact : undefined,
				contact_id: form.contact ? orderDocument.contact_id : undefined,
			};

			history.push({
				pathname: `/${organization.id}/documents/sales/new`,
				state: {
					copiedItem,
				},
			});
		}

		if (type === "stockAdjustment") {
			const stockAdjustmentDocument = JSON.parse(JSON.stringify(document)) as StockAdjustmentApi;
			const copiedItem = {
				adjusted_at: formatDateToString(new Date()),
				reference_number: form.referenceNumber ? stockAdjustmentDocument.reference_number : "",
				file_attachments: [],
				id: null,
				custom_fields: form.customFields ? stockAdjustmentDocument.custom_fields : [],
				stock: form.stock ? stockAdjustmentDocument.stock : undefined,
				stock_id: form.stock ? stockAdjustmentDocument.stock_id : undefined,
				comment: form.comment ? stockAdjustmentDocument.comment : "",
				tags: form.tags ? stockAdjustmentDocument.tags : [],
				items: [
					...stockAdjustmentDocument.items.map((item) => {
						return {
							...item,
							quantity: form.items.quantity ? item.quantity : undefined,
							volume: form.items.volume ? item.volume : undefined,
							price_net_money: form.items.price ? item.price_net_money : undefined,
						};
					}),
				],
				contact: form.contact ? stockAdjustmentDocument.contact : undefined,
				contact_id: form.contact ? stockAdjustmentDocument.contact_id : undefined,
			};

			history.push({
				pathname: `/${organization.id}/documents/stock_adjustments/new`,
				state: {
					copiedItem,
				},
			});
		}
	};

	return (
		<Modal onHide={onHide} show={show}>
			<Modal.Header closeButton>
				<Modal.Title>{`${t("lib:common.action.copy")} ${t("common.word.document").toLowerCase()}`}</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				<div className="mb-3">
					<span className="text-muted">{t("modules.purchase.field.choose_values_to_copy.title")}</span>
				</div>
				<Form.Check
					type="switch"
					checked={form.referenceNumber}
					label={t("common.word.reference_number")}
					onClick={() => handleOnClickFormCheck("referenceNumber")}
					className={isSmallBreakpoint ? "mb-2" : ""}
				/>
				<Form.Check
					type="switch"
					checked={form.stock}
					label={t("common.word.stock")}
					onClick={() => handleOnClickFormCheck("stock")}
					className={isSmallBreakpoint ? "mb-2" : ""}
				/>
				{type !== "transfer" && (
					<Form.Check
						checked={form.contact}
						type="switch"
						label={t("common.word.contact")}
						onClick={() => handleOnClickFormCheck("contact")}
						className={isSmallBreakpoint ? "mb-2" : ""}
					/>
				)}
				<Form.Check
					checked={form.comment}
					type="switch"
					label={t("common.word.comment.title")}
					onClick={() => handleOnClickFormCheck("comment")}
					className={isSmallBreakpoint ? "mb-2" : ""}
				/>
				<Form.Check
					checked={form.customFields}
					type="switch"
					label={t("common.word.custom_fields")}
					onClick={() => handleOnClickFormCheck("customFields")}
					className={isSmallBreakpoint ? "mb-2" : ""}
				/>
				<Form.Check
					checked={form.tags}
					type="switch"
					label={t("common.word.tags")}
					onClick={() => handleOnClickFormCheck("tags")}
					className={isSmallBreakpoint ? "mb-2" : ""}
				/>
				<span className="d-inline-block my-2">{t("common.word.items")}</span>
				<Form.Check
					checked={form.items.quantity}
					type="switch"
					label={t("common.word.quantity")}
					onClick={() => handleOnClickFormItemsCheck("quantity")}
					className={isSmallBreakpoint ? "mb-2" : ""}
				/>
				{type !== "bundle" && (
					<Form.Check
						checked={form.items.volume}
						type="switch"
						label={t("common.word.volume")}
						onClick={() => handleOnClickFormItemsCheck("volume")}
						className={isSmallBreakpoint ? "mb-2" : ""}
					/>
				)}
				{type !== "transfer" && type !== "bundle" && (
					<Form.Check
						checked={form.items.price}
						type="switch"
						label={t("modules.purchase.field.price.title")}
						onClick={() => handleOnClickFormItemsCheck("price")}
						className={isSmallBreakpoint ? "mb-2" : ""}
					/>
				)}
				{type !== "transfer" && type !== "stockAdjustment" && type !== "bundle" && (
					<Form.Check
						checked={form.items.tax}
						type="switch"
						label={t("common.word.tax")}
						onClick={() => handleOnClickFormItemsCheck("tax")}
						className={isSmallBreakpoint ? "mb-2" : ""}
					/>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button onClick={onSubmit} type="submit" onSubmit={onSubmit}>
					{t("lib:common.action.select")}
				</Button>
				<Button variant="light" onClick={onHide}>
					{t("lib:common.action.cancel")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
};

export default CopyDocumentModal;
